import * as RichEditApi from "devexpress-richedit";

// Pours a document's text into a membrete, producing the single .docx the
// system renders from.
//
// The membrete carries the header and footer (and the empty 1x2 header table
// RoyalEditorApi stamps the radicado and QR into) and no body; the text file
// carries only the body. The result is the membrete with its body replaced by
// the text, formatting included. The text file's own header and footer, if it
// has any, never make it across: only the body content is inserted.
//
// The core takes the RichEdit namespace as an argument instead of importing it,
// so the exact same code can be exercised in a bare browser page against the
// distributed bundle.

// Measured in time the page was visible. A hidden tab pauses the editor's
// rendering loop, so the merge simply stops until the admin comes back to the
// tab; counting that pause against the timeout failed every rebuild of an
// admin who switched tabs after replacing a membrete.
const MERGE_TIMEOUT_MS = 60000;
const TIMEOUT_TICK_MS = 1000;

export function mergeBodyIntoLetterhead(RichEdit, { letterhead, body }) {
  return new Promise((resolve, reject) => {
    // The editor needs a laid-out element even when nobody sees it.
    const host = document.createElement("div");
    Object.assign(host.style, {
      position: "fixed",
      left: "-10000px",
      top: "0",
      width: "816px",
      height: "1056px",
      visibility: "hidden",
    });
    document.body.appendChild(host);

    let rich = null;
    let settled = false;
    const finish = (error, base64) => {
      if (settled) return;
      settled = true;
      clearInterval(timer);
      try {
        if (rich) rich.dispose();
      } catch (_) {
        /* already torn down */
      }
      host.remove();
      if (error) reject(error);
      else resolve(base64);
    };
    let visibleMs = 0;
    let lastTick = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      if (document.visibilityState === "visible") visibleMs += now - lastTick;
      lastTick = now;
      if (visibleMs > MERGE_TIMEOUT_MS) {
        finish(new Error("La combinación del membrete con el texto tardó demasiado."));
      }
    }, TIMEOUT_TICK_MS);

    try {
      const options = RichEdit.createOptions();
      options.ribbon = { visible: false };
      options.width = "816px";
      options.height = "1056px";
      rich = RichEdit.create(host, options);

      rich.openDocument(letterhead, "membrete", RichEdit.DocumentFormat.OpenXml, (opened) => {
        if (!opened) return finish(new Error("No fue posible abrir el membrete."));
        try {
          const main = rich.document.subDocuments.main;
          // Whatever the membrete has in its body goes; the final paragraph
          // mark cannot be deleted and carries the section, so it stays.
          if (main.length > 1) main.deleteText(new RichEdit.Interval(0, main.length - 1));
          main.insertContent(0, body, RichEdit.DocumentFormat.OpenXml, (_interval, inserted) => {
            if (!inserted) return finish(new Error("No fue posible insertar el texto en el membrete."));
            try {
              rich.exportToBase64((base64) => finish(null, base64), RichEdit.DocumentFormat.OpenXml);
            } catch (e) {
              finish(e);
            }
          });
        } catch (e) {
          finish(e);
        }
      });
    } catch (e) {
      finish(e);
    }
  });
}

async function fetchArrayBuffer(url) {
  // no-store: a text or membrete is overwritten in place, and a cached copy
  // would rebuild the document from the previous version.
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) throw new Error(`No fue posible descargar ${url} (${response.status}).`);
  return response.arrayBuffer();
}

// Downloads the membrete and the text and merges them. Resolves to the merged
// .docx as base64, ready for saveFile.
export async function buildMergedDocument({ letterheadUrl, bodyUrl }) {
  const [letterhead, body] = await Promise.all([
    fetchArrayBuffer(letterheadUrl),
    fetchArrayBuffer(bodyUrl),
  ]);
  return mergeBodyIntoLetterhead(RichEditApi, { letterhead, body });
}
